import {
	OrderQuoteProductSyncKind,
	OrderQuoteProductSyncSeverity,
	type PreviewOrderQuoteProductSyncQuery,
	useSyncOrderQuoteProductsMutation,
} from '@codezo/graphql';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../../lib/utils';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';

type SyncPreview = NonNullable<PreviewOrderQuoteProductSyncQuery['previewOrderQuoteProductSync']>;
export type SyncPreviewLine = SyncPreview['lines'][number];

interface SyncProductFromCatalogueModalProps {
	orderQuoteId: string;
	/** The line being synced — `null` keeps the dialog closed. */
	line: SyncPreviewLine | null;
	onOpenChange: (open: boolean) => void;
	/** Fired once the snapshot has actually moved, so the quote can refetch. */
	onSynced: () => void;
}

/**
 * Re-copies one product from the catalogue onto its quote line.
 *
 * Confirmation rather than a bare menu action, because the frozen copy is the
 * record of what was quoted: replacing it is deliberate, and when a client can
 * already see a batch pricing this product it rewrites what they are reading.
 * Nothing priced is touched — not tiers, offers, plans or selections — so the
 * confirm is warned, never destructive.
 */
export function SyncProductFromCatalogueModal({
	orderQuoteId,
	line,
	onOpenChange,
	onSynced,
}: SyncProductFromCatalogueModalProps) {
	const [syncProducts, { loading }] = useSyncOrderQuoteProductsMutation();

	const needsReview = line?.severity === OrderQuoteProductSyncSeverity.Review;
	const batches = line?.clientVisibleBatches ?? [];
	const changes = line?.changes ?? [];

	const handleSync = async () => {
		if (!line) return;
		try {
			const { data } = await syncProducts({
				variables: {
					input: { orderQuoteId, orderQuoteItemIds: [line.orderQuoteItemId] },
				},
			});
			const res = data?.syncOrderQuoteProducts;
			if (!res?.success) {
				toast.error(res?.message || 'Failed to sync product');
				return;
			}
			/* Scoped to one line, so `partial` can only mean this line was the one
			   skipped — the product went unreadable between the preview and now.
			   Not `line.message`: that is the preview's drift text, describing what
			   WOULD have changed, which is the opposite of what happened. */
			if (res.messageKey?.endsWith('_partial')) {
				toast.error('The product could not be read, so the quote keeps its existing copy');
			} else if (res.messageKey?.endsWith('_nothing_to_do')) {
				toast.success('Already matches the catalogue');
			} else {
				toast.success('Product synced from catalogue');
			}
			onOpenChange(false);
			onSynced();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to sync product');
		}
	};

	return (
		<Dialog open={!!line} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Sync from catalogue</DialogTitle>
					<DialogDescription>
						Replaces this line&rsquo;s frozen copy of{' '}
						<span className="font-medium text-text-1">
							{line?.productName || 'the product'}
						</span>{' '}
						with the catalogue as it stands today. Prices, quantity tiers, offers and
						any client selections are untouched.
					</DialogDescription>
				</DialogHeader>

				<DialogBody>
					<div className="flex flex-col gap-4">
						{needsReview && batches.length > 0 && (
							<div className="flex items-start gap-2.5 rounded-[var(--r-3)] bg-status-warn/10 px-3 py-2.5 text-status-warn">
								<AlertTriangle size={15} className="mt-px shrink-0" />
								<div className="text-sm">
									The client can already see{' '}
									{batches.length === 1 ? 'a batch' : 'batches'} pricing this
									product —{' '}
									<span className="font-medium">
										{batches.map((b) => b.batchName).join(', ')}
									</span>
									. Syncing rewrites what they are reading.
								</div>
							</div>
						)}

						{line?.kind === OrderQuoteProductSyncKind.NeverSnapshotted && (
							<div className="rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-3 py-2.5 text-sm text-text-2">
								This line predates snapshotting, so there is almost nothing recorded
								on it. Syncing fills it from the catalogue as it stands today — the
								best available record, not the one that was quoted.
							</div>
						)}

						{changes.length > 0 && (
							<div className="flex flex-col gap-1.5">
								<div className="text-xs font-medium text-text-3">
									{changes.length === 1
										? '1 field would change'
										: `${changes.length} fields would change`}
								</div>
								<div className="overflow-hidden rounded-[var(--r-3)] border border-line-1">
									{changes.map((change, i) => (
										<div
											key={change.field}
											className="flex items-start gap-3 px-3 py-2 text-sm"
											style={{
												borderTop:
													i > 0 ? '0.5px solid var(--line-1)' : 'none',
											}}
										>
											<div className="w-28 shrink-0 text-text-3">
												{change.label}
											</div>
											<div className="flex min-w-0 flex-1 items-center gap-2 text-text-1">
												<span className="min-w-0 truncate text-text-4 line-through">
													{change.from || '—'}
												</span>
												<ArrowRight
													size={12}
													className="shrink-0 text-text-4"
												/>
												<span className="min-w-0 truncate">
													{change.to || '—'}
												</span>
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						<div className="text-xs text-text-4">
							{line?.snapshotAt
								? `Last checked against the catalogue ${formatDate(line.snapshotAt)}. `
								: ''}
							Image files are copied across, so this can take a moment.
						</div>
					</div>
				</DialogBody>

				<DialogFooter>
					<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button type="button" onClick={handleSync} disabled={loading}>
						{loading ? 'Syncing…' : 'Sync from catalogue'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
